// Add-to-cart toast. Config: #ShipToastConfig (layout/theme.liquid). Styles/markup: ship-toast-css.js, ship-toast-tpl.js.
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const cfg = document.getElementById('ShipToastConfig')?.dataset ?? {};
const m1 = Number(cfg.m1) * 100;
const m2 = Number(cfg.m2) * 100;
const SHOW_MS = Math.max(Number(cfg.ms) || 8000, 12000);
const reduce = matchMedia('(prefers-reduced-motion: reduce)');
const EASE = 'cubic-bezier(.32,.72,0,1)';
const OPEN = 'inset(-30px -30px -60px -30px round 58px)'; // negative insets: don't clip the rim + shadow
const money = (c) => formatMoney(c, cfg.moneyFormat, cfg.currency);
const t = (s, vars) => s.replace(/\[(\w+)\]/g, (_, k) => vars[k] ?? '');

// Arabic counting: 1 → "منتج واحد", 2 → "منتجين", 3–10 → "N منتجات", 11+ → "N منتج"
function itemsLabel(n) {
  if (!cfg.ar) return t(n === 1 ? cfg.lItemsOne : cfg.lItems, { count: n });
  const word = n === 1 ? 'منتج واحد' : n === 2 ? 'منتجين' : n <= 10 ? `${n} منتجات` : `${n} منتج`;
  return t(cfg.lItems, { count: word });
}

// ponytail: sibling modules load right away with this file's ?v= (cache-busts together); a tap that beats them waits.
const rel = (n) => { const u = new URL(n, import.meta.url); u.search = new URL(import.meta.url).search; return u.href; };
let cssText, tpl, assetsP;
const loadAssets = () => (assetsP ||= Promise.all([import(rel('ship-toast-css.js')), import(rel('ship-toast-tpl.js'))])
  .then(([c, h]) => { cssText = c.default; tpl = h.default; }));
loadAssets();

const root = window.Shopify?.routes?.root || '/';
const L = cfg.ar
  ? { added: 'اتضاف للسلة', adding: 'بنضيف للسلة…', recs: 'ناس كتير بتضيف دول معاه', keep: 'كمّل تسوّق', checkout: 'إتمام الطلب',
      add: 'أضف [title]', added1: 'اتضاف', err: 'مش متاح دلوقتي' }
  : { added: 'Added to your cart', adding: 'Adding to your cart…', recs: 'People add these with it', keep: 'Keep shopping', checkout: 'Checkout',
      add: 'Add [title]', added1: 'Added', err: 'Not available right now' };
const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const shortTitle = (s) => String(s || '').split(' — ')[0];

// the add event already carries the cart; GET /cart.js is only the fallback for emitters that don't
async function readCart(res, vid) {
  const c = res?.cart;
  if (c?.cost?.totalAmount) {
    return {
      total: Math.round(Number(c.cost.totalAmount.amount) * 100),
      count: c.totalQuantity,
      ids: new Set((c.lines || []).map((l) => String(l.id).split(':')[0])),
      first: (res.detail?.items || []).find((i) => String(i.variant_id) === vid) || res.detail?.items?.[0],
    };
  }
  const j = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  return { total: j.total_price, count: j.item_count, ids: new Set(j.items.map((i) => String(i.variant_id))), first: res?.detail?.items?.[0] || j.items[0] };
}

// ponytail: Shopify's own recommendations endpoint, both intents in one round trip, cached per product
const recCache = new Map();
function recsFor(pid) {
  if (!recCache.has(pid)) {
    const get = (intent) => fetch(`${root}recommendations/products.json?product_id=${pid}&limit=6&intent=${intent}`)
      .then((r) => (r.ok ? r.json() : { products: [] })).then((j) => j.products || []).catch(() => []);
    recCache.set(pid, Promise.all([get('complementary'), get('related')]).then(([a, b]) => a.concat(b)));
  }
  return recCache.get(pid);
}

let last = null;
let recsKey = null;
let recsBusy = false;
async function loadRecs(n) {
  const pid = n.first?.product_id;
  const wrap = el.querySelector('.ship-toast__recs');
  if (!pid) { wrap.hidden = true; return; }
  if (recsKey === pid) return;
  recsKey = pid;
  const seen = new Set();
  const list = (await recsFor(pid))
    .filter((p) => p.available && p.id !== pid && !p.variants.some((v) => n.ids.has(String(v.id))) && !seen.has(p.id) && seen.add(p.id))
    .slice(0, 2);
  if (recsKey !== pid) return;
  wrap.hidden = list.length < 2;
  el.classList.toggle('has-recs', !wrap.hidden);
  el.querySelector('.ship-toast__grid').innerHTML = list.map((p) => {
    const v = p.variants.find((x) => x.available) || p.variants[0];
    const img = p.featured_image || '';
    const src = img ? `${img}${img.includes('?') ? '&' : '?'}width=360` : '';
    return `<div class="ship-toast__rec">
      <a href="${esc(p.url)}" tabindex="-1" aria-hidden="true">${src ? `<img src="${esc(src)}" alt="" loading="lazy" width="180" height="135">` : ''}</a>
      <a href="${esc(p.url)}" class="ship-toast__rec-t">${esc(shortTitle(p.title))}</a>
      <div class="ship-toast__rec-row"><span class="ship-toast__rec-p">${esc(money(v.price))}</span>
      <button type="button" class="ship-toast__add" data-variant="${v.id}" aria-label="${esc(t(L.add, { title: shortTitle(p.title) }))}">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></button></div></div>`;
  }).join('');
  if (!wrap.hidden) placeAndGetSeed();
}

async function onRecClick(e) {
  const btn = e.target.closest('.ship-toast__add');
  if (!btn || recsBusy || btn.classList.contains('is-added')) return;
  recsBusy = true;
  btn.setAttribute('aria-busy', 'true');
  clearTimeout(timer);
  try {
    const j = await window.cgsAdd(btn.dataset.variant); // cart-extras.js: adds, feeds the drawer, updates the header badge
    const it = j.items?.[0];
    if (it && last) paint({ ...last, total: last.total + it.final_line_price, count: last.count + it.quantity }, false); // instant; reconciled below
    btn.classList.add('is-added');
    btn.setAttribute('aria-label', L.added1);
    btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12 5 5 9-10"/></svg>';
    readCart({}).then((n) => paint(n, false)).catch(() => {});
  } catch {
    btn.classList.add('is-err'); // sold out / limit reached: say so instead of doing nothing
    btn.setAttribute('aria-label', L.err);
    setTimeout(() => btn.classList.remove('is-err'), 2500);
  }
  btn.removeAttribute('aria-busy');
  recsBusy = false;
  arm();
}

let el, glass, body, timer, hovering = false, anim = null, closing = false, hdr = null, hdrTimer, seq = 0;
const curHeader = () => hdr || headerRect();

function build() {
  const style = document.createElement('style');
  style.textContent = cssText;
  document.head.append(style);
  el = document.createElement('div');
  el.className = 'ship-toast';
  el.lang = document.documentElement.lang;
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.setAttribute('popover', 'manual'); // top layer: stays above open dialogs (quick-add, drawer)
  el.hidden = true;
  el.innerHTML = tpl(L, cfg, root);
  document.body.append(el);
  glass = el.querySelector('.ship-toast__glass');
  body = el.querySelector('.ship-toast__body');
  el.querySelector('.ship-toast__close').addEventListener('click', hide);
  el.querySelector('.ship-toast__ghost').addEventListener('click', hide);
  el.querySelector('.ship-toast__grid').addEventListener('click', onRecClick);
  el.querySelector('.ship-toast__cta-cart').addEventListener('click', (e) => {
    const drawer = document.getElementById('cart-drawer');
    if (typeof drawer?.open !== 'function') return; // falls back to the /cart link
    e.preventDefault();
    hide();
    drawer.open();
  });
  el.addEventListener('pointerenter', () => { hovering = true; clearTimeout(timer); });
  el.addEventListener('pointerleave', () => { hovering = false; arm(); });
  el.addEventListener('focusin', () => clearTimeout(timer));
  el.querySelector('.ship-toast__node').style.insetInlineStart = `${(m1 / m2) * 100}%`;
}

function headerRect() {
  const h = document.querySelector('.fgh-header');
  if (!h) return null;
  const r = h.getBoundingClientRect();
  const cs = getComputedStyle(h);
  if (r.bottom <= 0 || r.width === 0 || Number(cs.opacity) < 0.2 || cs.visibility === 'hidden') return null;
  return { r, radius: Math.min(parseFloat(cs.borderTopLeftRadius) || 999, r.height / 2) };
}

// places the toast over the header; returns the clip that makes it look exactly like the header pill
function placeAndGetSeed() {
  const vw = document.documentElement.clientWidth;
  let w = Math.min(420, vw - 24);
  const h = curHeader();
  const phone = h && h.r.width <= w + 40; // phones: exactly as wide as the header it grows from
  if (phone) w = h.r.width;
  const rtl = document.documentElement.dir === 'rtl';
  let left;
  let top;
  if (h) {
    top = h.r.top;
    left = phone ? h.r.left : rtl ? h.r.left : h.r.right - w; // wide desktop: grow out of the cart end of the bar
  } else {
    top = 12;
    left = vw / 2 - w / 2;
  }
  if (!phone) left = Math.max(12, Math.min(left, vw - w - 12));
  Object.assign(el.style, { left: `${left}px`, top: `${top}px`, width: `${w}px` });
  return seedClip();
}

function seedClip() {
  const box = el.getBoundingClientRect();
  const h = curHeader();
  const s = h && Math.abs(h.r.top - box.top) < 4
    ? { l: Math.max(0, h.r.left - box.left), r: Math.max(0, box.right - h.r.right), b: Math.max(0, box.height - h.r.height), rad: h.radius }
    : { l: (box.width - 140) / 2, r: (box.width - 140) / 2, b: box.height - 40, rad: 20 };
  return `inset(0px ${s.r}px ${s.b}px ${s.l}px round ${s.rad}px)`;
}

function arm() {
  clearTimeout(timer);
  if (!hovering) timer = setTimeout(hide, SHOW_MS);
}

function hide() {
  clearTimeout(timer);
  if (!el || el.hidden || closing) return;
  closing = true;
  seq++; // an add still in flight must not pop the toast back open
  clearTimeout(hdrTimer);
  hdrTimer = setTimeout(() => document.documentElement.classList.remove('ship-toast-open'), 330);
  const done = () => {
    el.hidden = true;
    if (el.matches(':popover-open')) el.hidePopover();
    closing = false;
    hdr = null;
    recsKey = null; // the next open renders its own recommendations
    document.documentElement.classList.remove('ship-toast-open');
  };
  if (reduce.matches) {
    el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160 }).finished.then(done);
    return;
  }
  const seed = seedClip();
  body.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 140, fill: 'forwards' });
  anim?.cancel();
  anim = glass.animate([{ clipPath: OPEN }, { clipPath: seed, opacity: 1, offset: 0.85 }, { clipPath: seed, opacity: 0 }], {
    duration: 460, delay: 80, easing: EASE, fill: 'forwards',
  });
  anim.finished.then(done).catch(done);
}

function paint(n, withItem = true) {
  last = n;
  const total = n.total;
  if (withItem && n.first) {
    const it = n.first;
    el.querySelector('.ship-toast__added-item').innerHTML = `<b>${esc(shortTitle(it.product_title))}</b><span>${esc(money(it.final_line_price / it.quantity))}</span>`;
  }
  el.querySelector('.ship-toast__label').textContent = itemsLabel(n.count);
  el.querySelector('.ship-toast__total').textContent = money(total);
  el.querySelector('.ship-toast__node').classList.toggle('is-reached', total >= m1);
  el.querySelector('.ship-toast__node--end').classList.toggle('is-reached', total >= m2);
  el.classList.toggle('is-done', total >= m2);
  [[1, m1], [2, m2]].forEach(([i, at]) => {
    const row = el.querySelector(`.ship-toast__step[data-step="${i}"]`);
    row.classList.toggle('is-reached', total >= at);
    row.querySelector('.ship-toast__step-v').textContent = money(at);
  });
  el.querySelector('.ship-toast__checkout').textContent = `${L.checkout} · ${money(total)}`;
  el.querySelector('.ship-toast__fill').style.width = `${Math.min(100, (total / m2) * 100)}%`;
}

function open() {
  if (!el) build();
  const wasOpen = !el.hidden && !closing;
  closing = false;
  clearTimeout(hdrTimer);
  el.classList.add('is-adding');
  el.querySelector('.ship-toast__added-title').textContent = L.adding;
  if (!wasOpen) {
    hdr = headerRect();
    el.querySelector('.ship-toast__added-item').textContent = '';
    el.querySelector('.ship-toast__label').textContent = '';
    el.querySelector('.ship-toast__total').textContent = '—';
    el.querySelectorAll('.ship-toast__step,.ship-toast__node').forEach((n) => n.classList.remove('is-reached'));
    el.querySelectorAll('.ship-toast__step-v').forEach((n) => (n.textContent = ''));
    el.querySelector('.ship-toast__fill').style.width = '0%';
    el.querySelector('.ship-toast__recs').hidden = true;
    el.classList.remove('has-recs', 'is-done');
    el.querySelector('.ship-toast__checkout').textContent = L.checkout;
  }
  document.documentElement.classList.add('ship-toast-open');
  el.hidden = false;
  if (el.showPopover && !el.matches(':popover-open')) el.showPopover();
  const seed = placeAndGetSeed();
  if (!wasOpen) {
    anim?.cancel();
    body.getAnimations().forEach((a) => a.cancel());
    if (reduce.matches) {
      el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160 });
    } else {
      anim = glass.animate([{ clipPath: seed }, { clipPath: OPEN }], { duration: 560, easing: EASE });
      body.animate([{ opacity: 0, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'none' }], { duration: 260, delay: 200, easing: 'ease-out', fill: 'backwards' });
    }
  }
  arm(); // failsafe: never stay open if the add never settles
}

function begin(e) {
  if (!e.promise) return;
  const my = ++seq;
  const vid = String(e.lines?.[0]?.merchandiseId || '').split('/').pop();
  const form = e.target instanceof Element ? e.target.closest('product-form-component') : null;
  if (form?.dataset.productId) recsFor(Number(form.dataset.productId)); // warm recommendations while the add is in flight
  loadAssets().then(() => my === seq && open()).catch(() => {});
  e.promise
    .then(async (res) => {
      if (my !== seq) return;
      if (res?.detail?.didError) return hide();
      const n = await readCart(res, vid);
      await loadAssets();
      if (my !== seq) return;
      if (!el || el.hidden) open();
      el.classList.remove('is-adding');
      el.querySelector('.ship-toast__added-title').textContent = L.added;
      paint(n);
      loadRecs(n);
      arm();
    })
    .catch(() => { if (my === seq) hide(); });
}

// ponytail: the toast replaces the drawer's auto-open; delete to restore it.
const stopAutoOpen = () => document.querySelector('cart-drawer-component[auto-open]')?.removeAttribute('auto-open');
stopAutoOpen();

document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
  stopAutoOpen();
  if (e.action === 'add' && m2) begin(e);
});

document.addEventListener('keydown', (e) => e.key === 'Escape' && hide());

// the welcome-offer modal waits while the toast or the cart drawer is open
customElements.whenDefined('welcome-offer').then((C) => {
  const openWelcome = C.prototype.open;
  C.prototype.open = function (...a) {
    if (!this.designMode && (document.documentElement.classList.contains('ship-toast-open') || document.querySelector('theme-drawer[open]'))) {
      setTimeout(() => this.open(...a), 2500);
      return;
    }
    return openWelcome.apply(this, a);
  };
});